import type { KeyboardEvent, MouseEvent } from 'react';
import { ActionIcon, Card, Group, Text } from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faBellSlash,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { NudgeAgentIcon } from './NudgeAgentIcon';
import { ProactiveNudgeBody } from './ProactiveNudgeBody';
import { useNudgeOpen } from './useNudgeOpen';
import { dismissNudge, muteNudgeTask } from './nudgeActions';
import type { Nudge } from './nudgeStore';
import styles from './NudgeCard.module.css';

export interface NudgeCardProps {
  readonly nudge: Nudge;
}

/**
 * One proactive nudge as a clickable card: a shaded title bar (agent icon +
 * title, popout + dismiss) over the summary. Clicking anywhere — or the popout —
 * opens the conversation; the dismiss (X) stops propagation so it only closes.
 */
export function NudgeCard({ nudge }: NudgeCardProps) {
  const openConversation = useNudgeOpen();
  const open = () => openConversation(nudge);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  };

  const stop = (fn: () => void) => (e: MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  return (
    <Card
      className={styles.card}
      shadow="md"
      withBorder
      padding={0}
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={onKeyDown}
    >
      <Group
        justify="space-between"
        align="center"
        wrap="nowrap"
        className={styles.titleBar}
      >
        <Group gap="xs" align="center" wrap="nowrap">
          <NudgeAgentIcon agentId={nudge.agentId} />
          <Text fw={600} size="sm">
            {nudge.title}
          </Text>
        </Group>
        <Group gap={2} align="center" wrap="nowrap">
          <ActionIcon
            aria-label={`Open ${nudge.title}`}
            variant="subtle"
            size="sm"
            onClick={stop(open)}
          >
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </ActionIcon>
          {nudge.taskId && (
            <ActionIcon
              aria-label={`Stop showing me ${nudge.title}`}
              title="Stop showing me this"
              variant="subtle"
              size="sm"
              onClick={stop(() => muteNudgeTask(nudge.id, nudge.taskId))}
            >
              <FontAwesomeIcon icon={faBellSlash} />
            </ActionIcon>
          )}
          <ActionIcon
            aria-label={`Dismiss ${nudge.title}`}
            variant="subtle"
            size="sm"
            onClick={stop(() => dismissNudge(nudge.id))}
          >
            <FontAwesomeIcon icon={faXmark} />
          </ActionIcon>
        </Group>
      </Group>
      <div className={styles.body}>
        <ProactiveNudgeBody summary={nudge.summary} />
      </div>
    </Card>
  );
}
